import { useState } from "react";
import { useBranding, BRAND_DEFAULTS } from "../context/BrandingContext";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, Palette } from "lucide-react";

const COLOR_FIELDS = [
  ["gold", "Gold Accent", "Buttons, highlights, focus rings"],
  ["background", "Background", "Page foundation"],
  ["surface", "Surface / Cards", "Cards and dialogs"],
  ["surface_alt", "Surface Alt", "Calendar day cells, deep wells"],
  ["text", "Text", "Primary text"],
  ["muted", "Muted Text", "Secondary text"],
  ["border", "Borders", "Dividers and outlines"],
  ["success", "Success / Going", "Approved states, RSVP going"],
  ["danger", "Danger / Rejected", "Delete, reject, can't-go states"],
];

const IMAGE_SLOTS = [
  ["header_logo", "Header Logo", 44],
  ["login_crest", "Login Crest", 96],
  ["background", "Page Background", null],
];

const ASSET_OPTIONS = ["/brand/flag.png", "/brand/crest.png", "/brand/snake-logo.png", "/brand/wordmark.png"];

function Slider({ label, value, min, max, onChange, testid }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-neutral-400">{label}</span>
        <span className="font-mono text-[var(--sn-gold)]">{value}</span>
      </div>
      <input type="range" min={min} max={max} value={value} data-testid={testid}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-[#FFEC69]" />
    </div>
  );
}

export default function BrandingStudio() {
  const { branding, setBranding } = useBranding();
  const [saving, setSaving] = useState(false);

  const setColor = (k, v) => setBranding({ ...branding, colors: { ...branding.colors, [k]: v } });
  const setImg = (slot, k, v) =>
    setBranding({ ...branding, images: { ...branding.images, [slot]: { ...branding.images[slot], [k]: v } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding", { colors: branding.colors, images: branding.images });
      toast.success("Branding saved — live for everyone");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = () => {
    setBranding(JSON.parse(JSON.stringify(BRAND_DEFAULTS)));
    toast.success("Reset to Sigma Nu defaults — hit Save to persist");
  };

  return (
    <div className="space-y-6" data-testid="branding-studio">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[var(--sn-gold)] flex items-center gap-2">
            <Palette className="w-6 h-6" /> Branding Studio
          </h2>
          <p className="text-neutral-500 text-sm">Changes preview live. Hit Save to publish them for every member.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="branding-reset-button" variant="outline" onClick={reset}
            className="border-neutral-700 text-neutral-300 gap-2">
            <RotateCcw className="w-4 h-4" /> Reset
          </Button>
          <Button data-testid="branding-save-button" onClick={save} disabled={saving}
            className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Card className="glass-card p-5">
        <div className="text-xs uppercase tracking-widest text-[var(--sn-muted)] font-semibold mb-4">Color Palette</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COLOR_FIELDS.map(([key, label, hint]) => (
            <div key={key} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 p-3">
              <input type="color" value={branding.colors[key] || "#000000"}
                onChange={(e) => setColor(key, e.target.value)}
                data-testid={`branding-color-${key}`}
                className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10" />
              <div className="flex-1">
                <div className="text-sm text-neutral-200">{label}</div>
                <div className="text-[11px] text-neutral-500">{hint}</div>
              </div>
              <Input value={branding.colors[key] || ""} onChange={(e) => setColor(key, e.target.value)}
                data-testid={`branding-hex-${key}`}
                className="w-24 font-mono text-xs bg-neutral-900 border-white/10" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="glass-card p-5">
        <div className="text-xs uppercase tracking-widest text-[var(--sn-muted)] font-semibold mb-4">Brand Assets — click a slot to assign</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ASSET_OPTIONS.map(src => (
            <div key={src} className="rounded-lg border border-white/10 bg-black/40 p-3 space-y-2" data-testid={`asset-${src.split("/").pop()}`}>
              <div className="h-16 flex items-center justify-center">
                <img src={src} alt={src} className="max-h-16 w-auto object-contain" />
              </div>
              <div className="text-[10px] text-neutral-500 font-mono truncate">{src.replace("/brand/", "")}</div>
              <div className="flex flex-wrap gap-1">
                {[["header_logo", "Header"], ["login_crest", "Login"], ["background", "Bg"]].map(([slot, lbl]) => (
                  <button key={slot} type="button" data-testid={`assign-${slot}-${src.split("/").pop()}`}
                    onClick={() => setImg(slot, "src", src)}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 hover:border-[var(--sn-gold)] hover:text-[var(--sn-gold)] transition">
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {IMAGE_SLOTS.map(([slot, label, basePx]) => {
        const img = branding.images[slot] || {};
        return (
          <Card key={slot} className="glass-card p-5" data-testid={`branding-slot-${slot}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-sm font-semibold text-neutral-100">{label}</div>
                {slot === "background" && (
                  <div className="flex items-center gap-2 mt-2">
                    <Switch checked={!!img.enabled} onCheckedChange={(v) => setImg(slot, "enabled", v)}
                      data-testid="branding-bg-enabled" />
                    <span className="text-xs text-neutral-400">Show behind all pages</span>
                  </div>
                )}
              </div>
              {img.src && slot !== "background" && (
                <img src={img.src} alt={label} className="h-12 w-auto object-contain rounded bg-black/40 p-1" />
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Image</Label>
                <Select value={ASSET_OPTIONS.includes(img.src) ? img.src : "custom"}
                  onValueChange={(v) => v !== "custom" && setImg(slot, "src", v)}>
                  <SelectTrigger data-testid={`branding-src-${slot}`} className="bg-neutral-900 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-700">
                    {ASSET_OPTIONS.map(o => <SelectItem key={o} value={o}>{o.replace("/brand/", "")}</SelectItem>)}
                    <SelectItem value="custom">Custom URL…</SelectItem>
                  </SelectContent>
                </Select>
                {!ASSET_OPTIONS.includes(img.src) && (
                  <Input value={img.src || ""} onChange={(e) => setImg(slot, "src", e.target.value)}
                    data-testid={`branding-custom-src-${slot}`} placeholder="https://… or /brand/file.png"
                    className="bg-neutral-900 border-white/10 font-mono text-xs" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <Slider label="Size %" value={img.size ?? 100} min={10} max={300}
                  onChange={(v) => setImg(slot, "size", v)} testid={`branding-size-${slot}`} />
                <Slider label="Opacity %" value={img.opacity ?? 100} min={0} max={100}
                  onChange={(v) => setImg(slot, "opacity", v)} testid={`branding-opacity-${slot}`} />
                <Slider label={slot === "background" ? "Position X %" : "Offset X px"} value={img.x ?? 0}
                  min={slot === "background" ? 0 : -200} max={slot === "background" ? 100 : 200}
                  onChange={(v) => setImg(slot, "x", v)} testid={`branding-x-${slot}`} />
                <Slider label={slot === "background" ? "Position Y %" : "Offset Y px"} value={img.y ?? 0}
                  min={slot === "background" ? 0 : -200} max={slot === "background" ? 100 : 200}
                  onChange={(v) => setImg(slot, "y", v)} testid={`branding-y-${slot}`} />
                <Slider label="Z-Order" value={img.z ?? 10} min={0} max={50}
                  onChange={(v) => setImg(slot, "z", v)} testid={`branding-z-${slot}`} />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
